// @ts-nocheck
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileBottomNav, { type NavItem } from '../../components/mobile/MobileBottomNav';
import { NotificationCenter } from '../../components/common/NotificationCenter/NotificationCenter';
import { getFeaturedAds } from '../../services/adsApi';
import type { Notification } from '../../services/notificationsApi';
import { useUnreadNotifications } from '../../hooks/useUnreadNotifications';
import { useMarquees } from '../../hooks/useMarquees';
import styles from './MobileHomePage.module.css';
import { MobileLargeFeaturedCard } from '../../components/mobile/MobileLargeFeaturedCard';

interface ProductWithCardVariant {
  id: string;
  title: string;
  description: string | null;
  category: string;
  region: string;
  district: string | null;
  storeName: string;
  priceRange: string | null;
  imageUrls: string[];
  tags: string[];
  status: string;
  viewCount: number;
  clickCount: number;
  featured: number;              // 0=非精選, 1=一般精選, 2=大圖精選
  cardVariant: 'horizontal' | 'full-image';
}

export const MobileHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<ProductWithCardVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const { unreadCount } = useUnreadNotifications();
  const { enabled: marqueeEnabled } = useMarquees();

  // Create nav items with badge
  const navItems: NavItem[] = [
    {
      id: 'home',
      label: '首頁',
      path: '/',
      icon: '🏠',
    },
    {
      id: 'ads',
      label: '廣告',
      path: '/ads',
      icon: '🛒',
    },
    {
      id: 'wish',
      label: '願望',
      path: '/wishes',
      icon: '⭐',
    },
    {
      id: 'profile',
      label: '會員',
      path: '/member',
      icon: '👤',
      badge: unreadCount > 0 ? unreadCount.toString() : undefined,
    },
  ];

  useEffect(() => {
    fetchFeaturedAds();
  }, []);

  const fetchFeaturedAds = async () => {
    setLoading(true);
    try {
      // Fetch all featured ads (use a large number to effectively remove limit)
      const ads = await getFeaturedAds(1000);
      const featuredWithVariants = ads.map((ad, index) => {
        const variant = index === 0 ? 'full-image' : 'horizontal' as 'horizontal' | 'full-image';
        return { ...ad, cardVariant: variant } as ProductWithCardVariant;
      });
      setFeaturedProducts(featuredWithVariants);
    } catch (error) {
      console.error('Failed to fetch featured ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`/ads/${productId}`);
  };

  const handleSearch = () => {
    navigate('/ads?advanced=true');
  };

  const handleNotificationClick = (notification: Notification) => {
    console.log('Notification clicked:', notification);
  };

  return (
    <div className={styles.mobileHomePage}>
      <MobileHeader
        title="瑋瑋車業"
        showBack={false}
        showSearch
        showMarquee={marqueeEnabled}
        onSearch={handleSearch}
        rightAction={<NotificationCenter onNotificationClick={handleNotificationClick} />}
      />

      <div className={`${styles.mobileHomePage__content} ${marqueeEnabled ? styles['mobileHomePage__content--with-marquee'] : ''}`}>
        {/* Featured Services */}
        <section className={styles.mobileHomePage__section}>

          <div className={styles.mobileHomePage__serviceList}>
            {loading ? (
              <div className={styles.mobileHomePage__loading}>載入中...</div>
            ) : featuredProducts.length > 0 ? (
              featuredProducts.map((product, index) => {
                if (product.featured === 2) {
                  // Large featured ad - use MobileLargeFeaturedCard
                  return (
                    <MobileLargeFeaturedCard
                      key={product.id}
                      ad={product}
                      onClick={() => handleProductClick(product.id)}
                    />
                  );
                } else {
                  // Other featured ads - use horizontal variant
                  return (
                    <div
                      key={product.id}
                      className={styles.mobileHomePage__serviceCard}
                      onClick={() => handleProductClick(product.id)}
                    >
                      <div className={styles.mobileHomePage__serviceImage}>
                        {product.imageUrls && product.imageUrls.length > 0 ? (
                          <img src={product.imageUrls[0]} alt={product.title} />
                        ) : (
                          <span className={styles.mobileHomePage__servicePlaceholder}>🚗</span>
                        )}
                        {product.featured > 0 && (
                          <span className={styles.mobileHomePage__serviceBadge}>精選</span>
                        )}
                      </div>
                      <div className={styles.mobileHomePage__serviceContent}>
                        <h4 className={styles.mobileHomePage__serviceTitle}>{product.title}</h4>
                        {product.description && (
                          <p className={styles.mobileHomePage__serviceDescription}>{product.description}</p>
                        )}
                        <div className={styles.mobileHomePage__serviceMeta}>
                          <span className={styles.mobileHomePage__serviceStats}>
                            {product.viewCount}+人付款
                          </span>
                        </div>
                        <div className={styles.mobileHomePage__serviceFooter}>
                          <div className={styles.mobileHomePage__serviceProvider}>
                            <span className={styles.mobileHomePage__serviceProviderIcon}>✓</span>
                            <span className={styles.mobileHomePage__serviceProviderName}>
                              {product.storeName}
                            </span>
                          </div>
                          <span className={styles.mobileHomePage__serviceArrow}>→</span>
                        </div>
                      </div>
                    </div>
                  );
                }
              })
            ) : (
              <div className={styles.mobileHomePage__empty}>暫無推薦服務</div>
            )}
          </div>
        </section>
      </div>

      <MobileBottomNav items={navItems} />
    </div>
  );
};

export default MobileHomePage;
